import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextBody, TextHeadline, TextLabel2 } from '@cbhq/cds-web/typography';
import { ListCell } from '@cbhq/cds-web/cells/ListCell';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { memo, useCallback } from 'react';
import { ExploreListTokensMedia } from '@/components/Explore/ExploreListTokensMedia';
import { ExplorePoolData } from '@/components/Explore/hooks/useExploreData';
import { DisplayType, format } from '@/utils/numberFormatter';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';

type Props = {
  pool: ExplorePoolData;
  onSelect?: (poolId: string) => void;
};

/**
 * ExploreListMobileRow - Mobile view for a single pool in the explore list
 *
 * Displays pool information in a compact ListCell format optimized for mobile screens.
 * Shows token pair, fee tier, USDC rewards badge (if applicable), TVL, and APR.
 */
export const ExploreListMobileRow = memo(function ExploreListMobileRow({ pool, onSelect }: Props) {
  const { name, baseSymbol, quoteSymbol, tvlNotional, aprPercentage } = pool;

  // Mock fee percent - will be replaced with real data
  const feePercent = 0.0005; // 0.05%

  // Check if this is a USDC pair
  const isUSDCTokenPair = quoteSymbol === 'USDC' || baseSymbol === 'USDC';

  const handleClick = useCallback(() => {
    onSelect?.(pool.poolId);
  }, [onSelect, pool.poolId]);

  return (
    <ListCell
      testID={`explore-pool-row-${pool.poolId}`}
      justifyContent="space-between"
      onClick={handleClick}
      media={<ExploreListTokensMedia size="xl" baseSymbol={baseSymbol} quoteSymbol={quoteSymbol} />}
      title={<TextHeadline as="span">{name}</TextHeadline>}
      description={
        <HStack gap={0.5}>
          <Tag intent="informational" colorScheme="gray">
            {format({ value: feePercent, type: DisplayType.Percentage })}
          </Tag>
          {isUSDCTokenPair && <USDCRewardsChip showTooltip={false} />}
        </HStack>
      }
      detail={
        <VStack alignItems="flex-end" gap={0.5}>
          <TextBody as="span">
            {format({ value: tvlNotional, type: DisplayType.FiatShort })}
          </TextBody>
          <TextLabel2 as="span" color="fgMuted">
            {format({ value: aprPercentage, type: DisplayType.Percentage })} APR
          </TextLabel2>
        </VStack>
      }
    />
  );
});
